// brain.ts — the AI client. Calls the LIVE Oriora server brain for this app:
//   https://api.orioralabs.com/api/apps/oriora_curio/<action>
// with the customer's Oriora session token. The server holds the prompt + bills the
// Oriora wallet (Managed AI / BYOK). Collection data stays local; only a DERIVED-SIGNALS
// slice (counts, set completeness, attribute breakdowns, value extremes from USER-ENTERED
// values, cadence, duplicates, wishlist — see signals.rs) is sent per call. Raw rows and
// free-text notes never leave the device.
//
// THREE one-call actions, EACH a single billed call over the same derived slice:
//   • recap   — the numbers narrated (count, value, set completeness, recent adds)
//   • reflect — what the collection reveals about the collector (taste / throughline)
//   • ideas   — next-step directions grounded ONLY in their own catalogue
//
// HARDEST guardrails (enforced server-side, mirrored in our UI copy):
//   • COLLECTION fence — pure inventory narration over the user's OWN entered data.
//   • Value = a ROUGH, HEDGED orientation only (updated 2026-09-06, Bogdan "A") — never definitive/as-fact.
//     "What's it worth" is ANSWERED as "commonly lists around $X, I'm an AI, get a real appraisal"
//     (cited), NOT refused; user-logged values narrated as the user's own. See CURIO_SCOPE in chat/brain.ts.
//   • No buy/sell/trade advice, no resale facilitation, no marketplace.
//   • Descriptive only — no advice. AI-disclosure. No crypto / NSFW.
// We do not rebuild the brain; we compute the signals, call it, render the result.

import { invoke } from "./tauri-web";
import { getAccessToken } from "./supabase";
import type { CollectionSignals } from "./store";
import { searchWeb, readPage, readResults, formatReadResults, type WebResult } from "./search";

const SERVER = "https://api.orioralabs.com";
const SLUG = "oriora_curio";

export type CurioAction = "recap" | "reflect" | "ideas";

export type BrainErrorCode = "insufficient_funds" | "managed_ai_blocked" | "unauthenticated" | "unavailable" | "no_store" | "too_large";
export class BrainError extends Error {
  code: BrainErrorCode;
  constructor(code: BrainErrorCode, message: string) {
    super(message);
    this.code = code;
    this.name = "BrainError";
  }
}

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

// SSE reader for the streaming fleet chat (request body {stream:true} → text/event-stream):
// `: open` / `: keepalive` comments and [DONE] are ignored, each `data: {"chunk":…}` event
// feeds onChunk, and the `data: {"done":true, …}` event carries the exact old JSON payload —
// returned where res.json() used to be, so all existing action/reply mapping stays untouched.
async function readSseChat(bodyStream: ReadableStream<Uint8Array>, onChunk: (t: string) => void): Promise<Record<string, unknown>> {
  const reader = bodyStream.getReader();
  const decoder = new TextDecoder();
  let buf = "";
  let done: Record<string, unknown> | null = null;
  for (;;) {
    const { value, done: eof } = await reader.read();
    if (eof) break;
    buf += decoder.decode(value, { stream: true });
    let nl: number;
    while ((nl = buf.indexOf("\n")) !== -1) {
      const line = buf.slice(0, nl).replace(/\r$/, "");
      buf = buf.slice(nl + 1);
      if (!line.startsWith("data:")) continue; // comment (": open"/": keepalive") or blank line
      const payload = line.slice(5).trim();
      if (payload === "[DONE]") continue;
      let ev: Record<string, unknown>;
      try { ev = JSON.parse(payload) as Record<string, unknown>; } catch { continue; }
      if (typeof ev.chunk === "string") onChunk(ev.chunk);
      else if (ev.done) done = ev;
    }
  }
  if (!done) throw new BrainError("unavailable", "The reply stream ended early — please try again.");
  // A streamed reply can end with a billing/auth error instead of a payload ({done:true, error, _status}).
  // Returning it as data dissolved the "Top up" message into an empty action (found live 2026-08-27) —
  // throw the same BrainError the buffered path would.
  if (typeof done.error === "string" && done.error) {
    const st = typeof done._status === "number" ? done._status : 0;
    const msg = typeof done.message === "string" && done.message ? done.message : done.error;
    if (st === 401) throw new BrainError("unauthenticated", msg);
    if (st === 402) throw new BrainError("insufficient_funds", msg);
    if (st === 403) throw new BrainError("managed_ai_blocked", msg);
    throw new BrainError("unavailable", msg);
  }
  return done;
}

export async function brainCall<T>(endpoint: string, body: unknown, onChunk?: (t: string) => void): Promise<T> {
  const token = await getAccessToken();
  if (!token) throw new BrainError("unauthenticated", "Please sign in to Oriora.");
  // Retry transient failures (502/503/504 + network). Hard errors (401/402/403) never retry.
  const MAX = 3;
  for (let attempt = 1; attempt <= MAX; attempt++) {
    let res: Response;
    try {
      res = await fetch(`${SERVER}/api/apps/oriora_curio/${endpoint}`, {
        method: "POST",
        headers: { "content-type": "application/json", authorization: `Bearer ${token}` },
        body: JSON.stringify(onChunk && body && typeof body === "object" ? { ...(body as Record<string, unknown>), stream: true } : body),
      });
    } catch {
      if (attempt < MAX) { await sleep(attempt * 800); continue; }
      throw new BrainError("unavailable", "AI is temporarily unavailable.");
    }
    if (res.status === 401) throw new BrainError("unauthenticated", "Your session expired — sign in again.");
    if (res.status === 402) throw new BrainError("insufficient_funds", "Your Oriora balance is empty — top up to keep using AI.");
    if (res.status === 403) throw new BrainError("managed_ai_blocked", (await res.json().catch(() => null))?.message || "Connect a vendor key in Oriora settings to use AI for this app.");
    if ([502, 503, 504].includes(res.status)) {
      if (attempt < MAX) { await sleep(attempt * 800); continue; }
      throw new BrainError("unavailable", "AI is busy — please try again.");
    }
    if (!res.ok) throw new BrainError("unavailable", "AI request failed.");
    // Streaming fleet chat: with onChunk the body carries stream:true and the server answers
    // as SSE — read it incrementally (readSseChat above); the done event IS the old JSON
    // payload. Anything that is not an event stream (old server, an error status body, a
    // proxy that buffered it away) falls through to the buffered path — never a dead end.
    if (onChunk && res.body && (res.headers.get("content-type") ?? "").includes("text/event-stream")) {
      return (await readSseChat(res.body, onChunk)) as unknown as T;
    }
    return (await res.json()) as T;
  }
  throw new BrainError("unavailable", "AI is temporarily unavailable.");
}

export interface CurioInsight {
  /** The narrated result for the requested action. */
  text: string;
}

const LABELS: Record<CurioAction, string> = {
  recap: "recap",
  reflect: "reflection",
  ideas: "ideas",
};

/**
 * Run ONE of the three AI actions over a collection's derived-signal slice.
 *
 * Each action is its OWN single billed call to POST /api/apps/oriora_curio/<action>
 * with the same `signals` body. The server prompt for each action is OBSERVATIONAL +
 * grounded only in the slice (operator task); it never states market value, never
 * advises, never recommends buying. Empty catalogue → we don't even call.
 */
export async function runAction(action: CurioAction, signals: CollectionSignals): Promise<CurioInsight> {
  if (signals.counts.total === 0) {
    throw new BrainError("unavailable", `Add a few items first — there's nothing to ${action} yet.`);
  }
  const r = await brainCall<Partial<CurioInsight> & { reflection?: string; recap?: string; ideas?: string }>(action, { signals });
  // Be liberal in what we accept from the server: prefer `text`, fall back to action-named keys.
  const text = String(r.text ?? r.reflection ?? r.recap ?? r.ideas ?? "").trim();
  if (!text) {
    throw new BrainError("unavailable", `The ${LABELS[action]} came back empty — please try again.`);
  }
  return { text };
}

// ── Cloud backup (the user owns their data) ─────────────────────────────────────
// The device builds a FAITHFUL backup locally (export_backup); these relay it to the user's OWN
// connected cloud store — their GitHub repo or Google Drive (connected once in the Oriora
// account). Oriora RETAINS nothing. Plain HTTP → works on iOS. Endpoints: the generic
// POST/GET /api/apps/oriora_curio/backup (shared server route).
export interface CloudBackupState { connected: boolean; provider?: string; data?: unknown }

async function authedFetch(endpoint: string, init?: RequestInit): Promise<Response> {
  const token = await getAccessToken();
  if (!token) throw new BrainError("unauthenticated", "Please sign in to Oriora.");
  return fetch(`${SERVER}/api/apps/${SLUG}/${endpoint}`, {
    ...init,
    headers: { ...(init?.headers ?? {}), authorization: `Bearer ${token}` },
  });
}

/** Save the local backup envelope to the user's connected cloud store. Throws BrainError('no_store')
 *  when nothing is connected, so the UI can point them at the Connections hub. */
export async function cloudBackupSave(envelope: unknown): Promise<{ provider: string }> {
  let res: Response;
  try {
    res = await authedFetch("backup", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ data: envelope }),
    });
  } catch { throw new BrainError("unavailable", "Couldn't reach Oriora — check your connection."); }
  if (res.status === 401) throw new BrainError("unauthenticated", "Your session expired — sign in again.");
  if (res.status === 409) throw new BrainError("no_store", "Connect GitHub or Google Drive in your Oriora account to back up to the cloud.");
  // 413 = over the standard path's size limit (photo-heavy backup). Not retryable — the caller
  // switches to the device-direct GitHub save, or tells the customer how to enable it.
  if (res.status === 413) throw new BrainError("too_large", "This backup is too large for the standard path.");
  if (!res.ok) throw new BrainError("unavailable", "Cloud backup failed — please try again.");
  return (await res.json()) as { provider: string };
}

/** Read the cloud backup for restore. `connected:false` when no store is connected.
 *  Pass `provider` to read a NAMED source (the RESTORE STORY's explicit restore) — the server
 *  409s rather than silently reading a different cloud than the one the customer asked for. */
export async function cloudBackupLoad(provider?: "github" | "gdrive"): Promise<CloudBackupState> {
  let res: Response;
  try { res = await authedFetch(provider ? `backup?provider=${provider}` : "backup"); }
  catch { throw new BrainError("unavailable", "Couldn't reach Oriora — check your connection."); }
  if (res.status === 401) throw new BrainError("unauthenticated", "Your session expired — sign in again.");
  if (res.status === 409) throw new BrainError("no_store", `${provider === "gdrive" ? "Google Drive" : "GitHub"} isn't connected in your Oriora account.`);
  if (!res.ok) throw new BrainError("unavailable", "Couldn't read your cloud backup.");
  return (await res.json()) as CloudBackupState;
}

/** The RESTORE STORY status line — which clouds are connected, which one backups WRITE to, and the
 *  dated backup each holds. Ground truth from the relay (CUSTOMER-CLOUD-STORAGE.md §THE RESTORE STORY). */
export interface CloudBackupSlot { backup_exists: boolean; updated_at: string | null; bytes: number | null }
export interface CloudBackupStatusInfo {
  connected: ("github" | "gdrive")[];
  writes_to: "github" | "gdrive" | null;
  slots: Partial<Record<"github" | "gdrive", CloudBackupSlot>>;
}
export async function cloudBackupStatus(): Promise<CloudBackupStatusInfo> {
  let res: Response;
  try { res = await authedFetch("backup/status"); }
  catch { throw new BrainError("unavailable", "Couldn't reach Oriora — check your connection."); }
  if (res.status === 401) throw new BrainError("unauthenticated", "Your session expired — sign in again.");
  if (!res.ok) throw new BrainError("unavailable", "Couldn't check your backup status.");
  return (await res.json()) as CloudBackupStatusInfo;
}

/** Value & rarity lookup for ONE item. Runs a REAL on-device search + page read first
 *  (search.ts, Option 3 / Pattern 7 — DuckDuckGo on the user's own device, $0, any vendor),
 *  then sends the formatted text to the server (/research) to cite from. `sources` are the
 *  client's OWN search results — real and deterministic, never something the model reported. */
export async function researchItem(item: { name: string; kind?: string; attrs?: Record<string, unknown> }): Promise<{ text: string; sources: WebResult[]; searched: boolean }> {
  // "price guide" (not "rarity") is what surfaces actual price pages — A/B-tested on DuckDuckGo
  // 2026-08-19 with a real 1893 Morgan dollar; openWebFirst because encyclopedic Tier-1 sources
  // cannot contain prices and were crowding the quota on the web surface.
  const query = `${item.name} ${item.kind ?? ""} value price guide`.trim();
  const results = await searchWeb(query, { openWebFirst: true });
  const read = results.length ? await readResults(results, 3) : [];
  const webContext = formatReadResults(read);

  let res: Response;
  try {
    res = await authedFetch("research", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ item, web_context: webContext }),
    });
  } catch { throw new BrainError("unavailable", "Couldn't reach Oriora — check your connection."); }
  if (res.status === 401) throw new BrainError("unauthenticated", "Your session expired — sign in again.");
  if (res.status === 402) throw new BrainError("insufficient_funds", "Your Oriora balance is empty — top up to use AI research.");
  if (res.status === 403) throw new BrainError("managed_ai_blocked", (await res.json().catch(() => null))?.message || "Connect a vendor key in Oriora settings to use AI for this app.");
  if (!res.ok) throw new BrainError("unavailable", "Research came back empty — please try again.");
  const j = (await res.json()) as { text?: string };
  const text = String(j.text ?? "").trim();
  if (!text) throw new BrainError("unavailable", "Research came back empty — please try again.");
  return { text, sources: read.map(({ title, url, snippet }) => ({ title, url, snippet })), searched: read.some((r) => r.text) };
}

export interface CompareResult { text: string; sources: WebResult[]; searched: boolean }

/** Find similar collections on the open web, seeded by this collection's own signals. Runs a
 *  REAL on-device search first (search.ts) — sources are the client's own results, never
 *  something the model reported. */
export async function compareCollection(signals: CollectionSignals): Promise<CompareResult> {
  const query = `${signals.collection_kind || signals.collection_name} collectors community forum`.trim();
  const results = await searchWeb(query);
  const read = results.length ? await readResults(results, 3) : [];
  const webContext = formatReadResults(read);

  let res: Response;
  try {
    res = await authedFetch("compare", {
      method: "POST", headers: { "content-type": "application/json" },
      body: JSON.stringify({ signals, web_context: webContext }),
    });
  } catch { throw new BrainError("unavailable", "Couldn't reach Oriora — check your connection."); }
  if (res.status === 401) throw new BrainError("unauthenticated", "Your session expired — sign in again.");
  if (res.status === 402) throw new BrainError("insufficient_funds", "Your Oriora balance is empty — top up to use AI.");
  if (res.status === 403) throw new BrainError("managed_ai_blocked", (await res.json().catch(() => null))?.message || "Connect a vendor key in Oriora settings to use AI for this app.");
  if (!res.ok) throw new BrainError("unavailable", "Comparison came back empty — please try again.");
  const j = (await res.json()) as { text?: string };
  const text = String(j.text ?? "").trim();
  if (!text) throw new BrainError("unavailable", "Comparison came back empty — please try again.");
  return { text, sources: read.map(({ title, url, snippet }) => ({ title, url, snippet })), searched: read.some((r) => r.text) };
}

/** Compare against ONE page the user pastes a link to — reads it on-device via the existing
 *  SSRF-hardened `web_fetch` Tauri command (search.ts's `readPage`), never a raw browser `fetch`. */
export async function compareLink(signals: CollectionSignals, url: string): Promise<CompareResult> {
  const pageText = await readPage(url.trim());
  if (!pageText) throw new BrainError("unavailable", "Couldn't read that link — check the URL and your connection.");
  const webContext = `\n\n[Page retrieved just now on the user's own device — use and cite it; it came from a link the user gave you]:\nURL: ${url.trim()}\n${pageText}`;

  let res: Response;
  try {
    res = await authedFetch("compare", {
      method: "POST", headers: { "content-type": "application/json" },
      body: JSON.stringify({ signals, web_context: webContext }),
    });
  } catch { throw new BrainError("unavailable", "Couldn't reach Oriora — check your connection."); }
  if (res.status === 401) throw new BrainError("unauthenticated", "Your session expired — sign in again.");
  if (res.status === 402) throw new BrainError("insufficient_funds", "Your Oriora balance is empty — top up to use AI.");
  if (res.status === 403) throw new BrainError("managed_ai_blocked", (await res.json().catch(() => null))?.message || "Connect a vendor key in Oriora settings to use AI for this app.");
  if (!res.ok) throw new BrainError("unavailable", "Comparison came back empty — please try again.");
  const j = (await res.json()) as { text?: string };
  const text = String(j.text ?? "").trim();
  if (!text) throw new BrainError("unavailable", "Comparison came back empty — please try again.");
  return { text, sources: [{ title: url.trim(), url: url.trim(), snippet: "" }], searched: true };
}

/** AI-worded catalog description of ONE item, grounded only in its own logged fields — no
 *  search, no value talk. Server route /describe. */
export async function describeItem(item: { name: string; kind?: string; attrs?: Record<string, unknown>; condition?: string }): Promise<{ text: string }> {
  let res: Response;
  try {
    res = await authedFetch("describe", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ item }),
    });
  } catch { throw new BrainError("unavailable", "Couldn't reach Oriora — check your connection."); }
  if (res.status === 401) throw new BrainError("unauthenticated", "Your session expired — sign in again.");
  if (res.status === 402) throw new BrainError("insufficient_funds", "Your Oriora balance is empty — top up to use AI.");
  if (res.status === 403) throw new BrainError("managed_ai_blocked", (await res.json().catch(() => null))?.message || "Connect a vendor key in Oriora settings to use AI for this app.");
  if (!res.ok) throw new BrainError("unavailable", "Description came back empty — please try again.");
  const j = (await res.json()) as { text?: string };
  const text = String(j.text ?? "").trim();
  if (!text) throw new BrainError("unavailable", "Description came back empty — please try again.");
  return { text };
}

/** Save arbitrary text — a note, or the whole conversation — to the user's OWN cloud (GitHub/Drive).
 *  Honors a named destination. Oriora retains nothing. Throws no_store when nothing is connected. */
export async function cloudStoreSave(
  content: string,
  opts?: { name?: string; kind?: string; destination?: "github" | "gdrive" },
): Promise<{ provider: string }> {
  let res: Response;
  try {
    res = await authedFetch("store", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ content, name: opts?.name, kind: opts?.kind, provider: opts?.destination }),
    });
  } catch { throw new BrainError("unavailable", "Couldn't reach Oriora — check your connection."); }
  if (res.status === 401) throw new BrainError("unauthenticated", "Your session expired — sign in again.");
  if (res.status === 409) throw new BrainError("no_store", "Connect GitHub or Google Drive in your Oriora account to save to the cloud.");
  if (!res.ok) throw new BrainError("unavailable", "Saving to your cloud failed — please try again.");
  return (await res.json()) as { provider: string };
}

// Login opens the ecosystem web login INSIDE the app (its own WebKit window), not the OS
// browser — the in-app https handback is robust on iOS where the custom-scheme deep-link is
// fragile. Session returns via auth:deep-link (App.tsx + ipc.rs). The web callback must
// allowlist `oriora-curio` as a return target (server-side, operator task).
export const openWebLogin = () => invoke<void>("open_login_window");

// ── IDENTIFY FROM PHOTO (§5b build 2026-08-19) — one photo → suggested values for THIS
// collection's OWN schema. Suggestions only; the form fills empty fields and the customer
// reviews before saving. The photo transits for this call only; Oriora doesn't retain it.
export async function identifyItem(input: {
  image: string; // downscaled JPEG data URL
  collection_name: string;
  attributes: { key: string; label: string }[];
}): Promise<{ name: string; values: Record<string, string>; confidence: string; note: string }> {
  let res: Response;
  try {
    res = await authedFetch("identify-item", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(input),
    });
  } catch { throw new BrainError("unavailable", "Couldn't reach Oriora — check your connection."); }
  if (res.status === 401) throw new BrainError("unauthenticated", "Your session expired — sign in again.");
  if (res.status === 402) throw new BrainError("insufficient_funds", "Your Oriora balance is empty — top up to use AI.");
  if (res.status === 403) throw new BrainError("managed_ai_blocked", (await res.json().catch(() => null))?.message || "Connect a vendor key in Oriora settings to use AI for this app.");
  if (res.status === 413) throw new BrainError("too_large", "That photo is too large — retake a smaller one.");
  if (!res.ok) throw new BrainError("unavailable", "The photo couldn't be read — please try again.");
  const j = (await res.json()) as { name?: string; values?: Record<string, string>; confidence?: string; note?: string };
  return {
    name: String(j.name ?? "").trim(),
    values: j.values && typeof j.values === "object" ? j.values : {},
    confidence: String(j.confidence ?? "low"),
    note: String(j.note ?? "").trim(),
  };
}
